import type { AxiosInstance } from 'axios';
import type { paths, components } from '../schema';
import type { RequestOptions } from './types';
import { curry1 } from './utils';
import type { RequestOptionsWithParams } from './types';

export type BillingGetInvoicesPage =
  paths['/api/billing/invoices']['get']['responses']['200']['content']['application/json'];

export type BillingGetInvoicesParams =
  paths['/api/billing/invoices']['get']['parameters']['query'];

export const getInvoicesPage = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<BillingGetInvoicesParams>
): Promise<BillingGetInvoicesPage> => {
  const res = await instance.get<BillingGetInvoicesPage>(
    `/billing/invoices`,
    options
  );

  return res.data;
};

export type BillingScheduledBusinessLicenses =
  paths['/api/billing/business-license/scheduled-business-license']['get']['responses']['200']['content']['application/json'];

export type BusinessLicensePlanAssociation =
  components['schemas']['BusinessLicensePlanAssociation'];

export const getScheduledBusinessLicenses = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingScheduledBusinessLicenses> => {
  const res = await instance.get<BillingScheduledBusinessLicenses>(
    '/billing/business-license/scheduled-business-license',
    options
  );

  return res.data;
};

export type BillingSettings =
  paths['/api/billing/settings']['get']['responses']['200']['content']['application/json'];

export const getBillingSettings = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingSettings> => {
  const res = await instance.get<BillingSettings>('/billing/settings', options);

  return res.data;
};

export type BillingUpdateSettingsPayload =
  components['schemas']['BillingSettingsWrite'];

export type BillingChangeToPlan =
  paths['/api/billing/business-license/change-to-plan']['post']['responses']['200']['content']['application/json'];

export type BillingChangeToPlanPayload =
  components['schemas']['ChangeBusinessToNewTemplatePlanViewRequest'];

export type BillingChangeToPlanError =
  paths['/api/billing/business-license/change-to-plan']['post']['responses']['400']['content']['application/json'];

export type BillingCreateSetupIntent =
  paths['/api/billing/payment/stripe/setup-intent']['post']['responses']['200']['content']['application/json'];

export const createSetupIntent = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingCreateSetupIntent> => {
  const res = await instance.post<BillingCreateSetupIntent>(
    '/billing/payment/stripe/setup-intent',
    options
  );

  return res.data;
};

export type BillingPaymentMethods =
  paths['/api/billing/payment/stripe/payment-methods']['get']['responses']['200']['content']['application/json'];

export const getPaymentMethods = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingPaymentMethods> => {
  const res = await instance.get<BillingPaymentMethods>(
    '/billing/payment/stripe/payment-methods',
    options
  );

  return res.data;
};

export type BillingSummary =
  paths['/api/billing/summary-usage']['get']['responses']['200']['content']['application/json'];

export const getSummaryUsage = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingSummary> => {
  const res = await instance.get<BillingSummary>(
    '/billing/summary-usage',
    options
  );

  return res.data;
};

export type BillingLicensePlans =
  paths['/api/billing/license-plans']['get']['responses']['200']['content']['application/json'];

export const getBillingLicensePlans = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingLicensePlans> => {
  const res = await instance.get<BillingLicensePlans>(
    '/billing/license-plans',
    options
  );

  return res.data;
};

export type BillingBusinessLicense =
  paths['/api/billing/business-license']['get']['responses']['200']['content']['application/json'];

export const getBusinessLicense = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingBusinessLicense> => {
  const res = await instance.get<BillingBusinessLicense>(
    '/billing/business-license',
    options
  );

  return res.data;
};

// 'k' = Kizen AI credits (raw count). 'u'/'p' = USD nano-dollars
// (1_000_000_000 = $1) as of the BE's cents -> nano-dollars rename.
export type AIUsageCostUnit = 'k' | 'u' | 'p';

export type AIUsageCycle = {
  usage_cycle: string;
  end_date: string;
};

export type BillingGetAIUsageCycles = {
  current_usage_cycle: string;
  results: AIUsageCycle[];
};

export const getAIUsageCycles = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<BillingGetAIUsageCycles> => {
  const res = await instance.get<BillingGetAIUsageCycles>(
    '/billing/ai-usage-cycles',
    options
  );

  return res.data;
};

export type AIUsageColumn = {
  key: string;
  llmprovider_id: string;
  // The provider's raw display_name (e.g. "Gemini"), undecorated — `label`
  // is the ready-to-render header composed from this and cost_unit (e.g.
  // "Gemini (via Kizen)"). Not currently used by the FE, but part of the
  // real response shape.
  llmprovider_display_name: string;
  cost_unit: AIUsageCostUnit;
  label: string;
  sort_key: string;
};

export type AIUsageAmount = {
  key: string;
  llmprovider_id: string;
  cost_unit: AIUsageCostUnit;
  cost_amount: number;
};

export type BillingGetAIUsageSummaryParams = {
  usage_cycle?: string;
};

export type BillingGetAIUsageSummary = {
  usage_cycle: string;
  columns: AIUsageColumn[];
  employees: AIUsageAmount[];
  agentic_workflows: AIUsageAmount[];
  system: AIUsageAmount[];
  total: AIUsageAmount[];
};

export const getAIUsageSummary = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<BillingGetAIUsageSummaryParams>
): Promise<BillingGetAIUsageSummary> => {
  const res = await instance.get<BillingGetAIUsageSummary>(
    '/billing/ai-usage-summary',
    options
  );

  return res.data;
};

export type BillingGetAIUsageBreakdownParams = {
  usage_cycle?: string;
  page?: number;
  page_size?: number;
  search?: string;
  sort?: string;
  id?: string;
};

export type AIUsageAgenticWorkflowEntity = {
  id: string;
  display_name: string;
  archived: boolean;
};

export type BillingGetAIUsageAgenticWorkflowsBreakdown = {
  count: number;
  next: string | null;
  previous: string | null;
  usage_cycle: string;
  columns: AIUsageColumn[];
  unattributed_usage: AIUsageAmount[];
  // Renamed from `automation` in the same BE commit.
  results: Array<{
    agentic_workflow: AIUsageAgenticWorkflowEntity;
    usage: AIUsageAmount[];
  }>;
};

export const getAIUsageAgenticWorkflowsBreakdown = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<BillingGetAIUsageBreakdownParams>
): Promise<BillingGetAIUsageAgenticWorkflowsBreakdown> => {
  const res = await instance.get<BillingGetAIUsageAgenticWorkflowsBreakdown>(
    '/billing/ai-usage-summary/agentic-workflows',
    options
  );

  return res.data;
};

export type AIUsageAccountType =
  | 'user_standard'
  | 'service_automation'
  | 'service_integration'
  | 'service_automationplugin'
  | 'service_integration_plugin';

export type AIUsageEmployeeEntity = {
  id: string;
  display_name: string;
  archived: boolean;
  account_type: AIUsageAccountType | null;
};

export type BillingGetAIUsageTeamMembersBreakdown = {
  count: number;
  next: string | null;
  previous: string | null;
  usage_cycle: string;
  columns: AIUsageColumn[];
  unattributed_usage: AIUsageAmount[];
  results: Array<{ employee: AIUsageEmployeeEntity; usage: AIUsageAmount[] }>;
};

export const getAIUsageTeamMembersBreakdown = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<BillingGetAIUsageBreakdownParams>
): Promise<BillingGetAIUsageTeamMembersBreakdown> => {
  const res = await instance.get<BillingGetAIUsageTeamMembersBreakdown>(
    '/billing/ai-usage-summary/team-members',
    options
  );

  return res.data;
};

export class BillingService {
  public getSummaryUsage;
  public getBillingLicensePlans;
  public getBusinessLicense;
  public getPaymentMethods;
  public createSetupIntent;
  public getInvoicesPage;
  public getAIUsageCycles;
  public getAIUsageSummary;
  public getAIUsageAgenticWorkflowsBreakdown;
  public getAIUsageTeamMembersBreakdown;

  constructor(private instance: AxiosInstance) {
    this.getSummaryUsage = curry1(getSummaryUsage)(this.instance);
    this.getBillingLicensePlans = curry1(getBillingLicensePlans)(this.instance);
    this.getBusinessLicense = curry1(getBusinessLicense)(this.instance);
    this.getPaymentMethods = curry1(getPaymentMethods)(this.instance);
    this.createSetupIntent = curry1(createSetupIntent)(this.instance);
    this.getInvoicesPage = curry1(getInvoicesPage)(this.instance);
    this.getAIUsageCycles = curry1(getAIUsageCycles)(this.instance);
    this.getAIUsageSummary = curry1(getAIUsageSummary)(this.instance);
    this.getAIUsageAgenticWorkflowsBreakdown = curry1(
      getAIUsageAgenticWorkflowsBreakdown
    )(this.instance);
    this.getAIUsageTeamMembersBreakdown = curry1(
      getAIUsageTeamMembersBreakdown
    )(this.instance);
  }
}
